import { AutoComplete, AutoCompleteProps } from "./AutoComplete";
import { useTRPC } from "#trpc";
import { useQuery } from "@tanstack/react-query";
import { forwardRef, useCallback, useMemo } from "react";
import {
  AutocompleteValue,
  createFilterOptions,
} from "@mui/material/Autocomplete";
import { email, pipe, safeParse, string } from "valibot";
import { IUser } from "@dmca/models/interface/user";
import { AutocompleteFreeSoloValueMapping } from "@mui/material";
import { ContactInfo } from "@dmca/utils/types";

type InnerValue =
  | {
      type: "userId" | "manual";
      title: string;
      value: string;
    }
  | {
      type: "manual-option";
      title: string;
      inputValue: string;
    };
type Value = ContactInfo;

export interface EmailAutoCompleteInputProps<
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends Omit<
    AutoCompleteProps<InnerValue, Multiple, DisableClearable, FreeSolo>,
    | "options"
    | "getOptionKey"
    | "freeSolo"
    | "onChange"
    | "loading"
    | "clearOnBlur"
    | "handleHomeEndKeys"
    | "filterOptions"
    | "isOptionEqualToValue"
    | "getOptionDisabled"
    | "value"
  > {
  value?: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo>;
  /** Allows the user to manually enter an email. Defaults to `false`. */
  acceptManualEmail?: FreeSolo;
  /** Filters out user options. */
  filterUsers?: (user: IUser) => boolean;
  onChange?: (
    event: React.SyntheticEvent,
    value: AutocompleteValue<Value, Multiple, DisableClearable, false>,
    reason: string,
    details?: any
  ) => void;
}

const filter = createFilterOptions<InnerValue>();

const emailSchema = pipe(string(), email());

function EmailAutoCompleteInputInner<
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>(
  {
    acceptManualEmail = false as FreeSolo,
    filterUsers,
    value,
    ...rest
  }: EmailAutoCompleteInputProps<Multiple, DisableClearable, FreeSolo>,
  ref: any
) {
  const trpc = useTRPC();
  const { data: users, isInitialLoading } = useQuery(
    trpc.user.getAll.queryOptions(undefined, {
      select: (users) =>
        users
          .filter((user) => {
            let valid = user.hasEmail && user.emailVerified;
            if (filterUsers) valid = valid && filterUsers(user);
            return valid;
          })
          .sort((a, b) => a.name.localeCompare(b.name)),
    })
  );

  const options = useMemo(() => {
    return (
      users?.map((user) => ({
        type: "userId" as const,
        value: user.id,
        title: user.name,
      })) || []
    );
  }, [users]);

  const innerValue = useMemo(() => {
    const convertValue = (
      val: AutocompleteValue<Value, false, DisableClearable, FreeSolo>
    ): InnerValue | null => {
      if (val == null) return null;
      if (typeof val === "string") {
        return {
          type: "manual",
          title: val,
          value: val,
        };
      }
      if (val.inputType === "manual") {
        return {
          type: "manual",
          title: val.value,
          value: val.value,
        };
      }
      return {
        type: "userId",
        title:
          options.find((o) => o.value === val.value)?.title || "Deleted User",
        value: val.value,
      };
    };
    if (value == null) {
      return (rest.multiple ? [] : null) as AutocompleteValue<
        InnerValue,
        Multiple,
        DisableClearable,
        FreeSolo
      >;
    }
    return (
      Array.isArray(value)
        ? value.map((val) => convertValue(val))
        : // @ts-expect-error type error
          convertValue(value)
    ) as AutocompleteValue<InnerValue, Multiple, DisableClearable, FreeSolo>;
  }, [options, value, rest.multiple]);

  const validateManualInput = useCallback(
    (check: string) => {
      const isValidEmail = safeParse(emailSchema, check).success;
      if (!isValidEmail) return false;
      const isInValue = innerValue
        ? Array.isArray(innerValue)
          ? innerValue.some((val) =>
              typeof val === "string"
                ? val === check
                : val.type === "manual" && val.value === check
            )
          : typeof innerValue === "string"
          ? innerValue === check
          : innerValue.type === "manual" && innerValue.value === check
        : false;
      if (isInValue) return false;
      return true;
    },
    [innerValue]
  );

  const getOptionLabel = useCallback(
    (option: InnerValue | AutocompleteFreeSoloValueMapping<FreeSolo>) => {
      if (option == null) return "";
      if (typeof option === "string") return option;
      if (option.type === "manual-option") {
        return option.title;
      }
      if (option.type === "manual") {
        return "inputValue" in option
          ? (option.inputValue as string)
          : option.value;
      }
      return "title" in option
        ? option.title
        : // @ts-expect-error type error
          options.find((x) => x.value === option.value)?.title ||
            "Deleted User";
    },
    [options]
  );

  const createFilter = useCallback(
    (options: InnerValue[], inputValue: string) => {
      const filtered = filter(options, {
        inputValue,
        getOptionLabel,
      });

      if (filtered.length > 0 || !acceptManualEmail) return filtered;

      if (
        inputValue !== "" &&
        ((!!innerValue &&
          inputValue !==
            ("value" in innerValue ? innerValue.value : innerValue)) ||
          !innerValue)
      ) {
        filtered.push({
          type: "manual-option",
          inputValue: inputValue,
          title: `Add ${inputValue}`,
        });
      }
      return filtered;
    },
    [acceptManualEmail, getOptionLabel, innerValue]
  );

  return (
    <AutoComplete
      ref={ref}
      loading={isInitialLoading}
      value={
        (isInitialLoading
          ? rest.multiple
            ? []
            : null
          : innerValue) as AutocompleteValue<
          InnerValue,
          Multiple,
          DisableClearable,
          FreeSolo
        >
      }
      {...rest}
      onKeyDown={(event) => {
        // Disables selecting the option when pressing Enter
        if (event.key === "Enter") {
          event.defaultMuiPrevented = true;
        }
        rest.onKeyDown?.(event);
      }}
      onChange={(event, newValue, reason, details) => {
        type TempType = AutocompleteValue<
          Value,
          Multiple,
          DisableClearable,
          false
        >;
        const calculateValue = (value: typeof newValue) => {
          if (value == null) return null;
          if (typeof value === "string") {
            return {
              inputType: "manual",
              value,
              contactType: "email",
            } as TempType;
          } else if (
            value &&
            "type" in value &&
            value.type === "manual-option"
          ) {
            // when manual email is added via clicking on the ui
            return {
              inputType: "manual",
              value: (value as any).inputValue,
              contactType: "email",
            } as TempType;
          } else {
            return {
              // @ts-expect-error type error
              inputType: value.type === "userId" ? "user" : "manual",
              contactType: "email",
              // @ts-expect-error type error
              value: value.value,
            } as TempType;
          }
        };
        const calculatedValue = (newValue == null
          ? rest.multiple
            ? []
            : null
          : Array.isArray(newValue)
          ? (newValue as (typeof newValue)[]).map(calculateValue)
          : calculateValue(newValue)) as unknown as TempType;
        rest.onChange?.(event, calculatedValue, reason, details);
      }}
      freeSolo={acceptManualEmail}
      clearOnBlur={acceptManualEmail}
      handleHomeEndKeys={acceptManualEmail}
      getOptionDisabled={(option) =>
        option.type !== "userId" &&
        !validateManualInput(
          "value" in option ? option.value : option.inputValue
        )
      }
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionKey={(option) =>
        typeof option === "string"
          ? option
          : "value" in option
          ? option.value
          : option.inputValue
      }
      filterOptions={(options, params) => {
        return createFilter(options, params.inputValue);
      }}
      isOptionEqualToValue={(option, value) => {
        if (option == null || value == null) return false;
        return (
          option.type !== "manual-option" &&
          (value as { value: string }).value === option.value
        );
      }}
      inputProps={{
        ...rest.inputProps,
        slotProps: {
          ...rest.inputProps?.slotProps,
          htmlInput: {
            ...rest.inputProps?.slotProps?.htmlInput,
            inputMode: "email",
          },
        },
      }}
    />
  );
}

export const EmailAutoCompleteInput = forwardRef(
  EmailAutoCompleteInputInner
) as <
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
>(
  props: EmailAutoCompleteInputProps<Multiple, DisableClearable, FreeSolo> & {
    ref?: React.ForwardedRef<HTMLDivElement>;
  }
) => ReturnType<typeof EmailAutoCompleteInputInner>;
